/**
 * Generated by orval v8.8.1 🍺
 * Do not edit manually.
 * OGP API
 * API for fetching Open Graph Protocol metadata from URLs
 * OpenAPI spec version: 1.0.0
 */
import * as zod from "zod";

/**
 * Fetches Open Graph Protocol metadata (title, description, image, site_name) from the provided URL.
Results are cached for 7 days to improve performance.

 * @summary Fetch OGP metadata from external URL
 */
export const fetchOgpBodyUrlMax = 1024;

export const FetchOgpBody = zod
    .object({
        url: zod.string().url().max(fetchOgpBodyUrlMax).describe("Target URL to fetch OGP metadata from"),
    })
    .strict();

export const fetchOgp200ResponseDataOgpTitleMax = 320;

export const fetchOgp200ResponseDataSiteNameMax = 100;

export const FetchOgp200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        data: zod
            .object({
                url: zod.string().url().optional().describe("Original URL"),
                ogp_title: zod
                    .string()
                    .max(fetchOgp200ResponseDataOgpTitleMax)
                    .optional()
                    .describe("Page title from og:title"),
                general_description: zod.string().optional().describe("Page description from og:description"),
                image: zod.string().optional().describe("Preview image URL from og:image"),
                site_name: zod
                    .string()
                    .max(fetchOgp200ResponseDataSiteNameMax)
                    .optional()
                    .describe("Site name from og:site_name"),
                cached_at: zod.string().datetime({ offset: true }).optional().describe("When this data was cached"),
                expires_at: zod
                    .string()
                    .datetime({ offset: true })
                    .optional()
                    .describe("When this cached data expires"),
                errors_at: zod
                    .string()
                    .datetime({ offset: true })
                    .nullish()
                    .describe("When the last OGP fetch error occurred"),
                error_expires_at: zod
                    .string()
                    .datetime({ offset: true })
                    .nullish()
                    .describe("Until when refetch is suppressed after an OGP fetch error"),
            })
            .strict()
            .optional(),
        cached: zod.boolean().optional().describe("Whether the result was served from cache"),
        message: zod.string().optional(),
    })
    .strict();

export const FetchOgp400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgp404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgp422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgp500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgp504Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

/**
 * Fetches Open Graph Protocol metadata from the provided URL and downloads the og:image
to the local public product OGP directory.

 * @summary Fetch OGP metadata and download og:image
 */
export const fetchOgpImageBodyUrlMax = 1024;

export const FetchOgpImageBody = zod
    .object({
        url: zod.string().url().max(fetchOgpImageBodyUrlMax).describe("Target URL to fetch OGP metadata from"),
    })
    .strict();

export const fetchOgpImage200ResponseImagePathMax = 150;

export const fetchOgpImage200ResponseImageUrlMax = 1024;

export const FetchOgpImage200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        image_path: zod
            .string()
            .max(fetchOgpImage200ResponseImagePathMax)
            .describe("Relative local path to the downloaded image"),
        image_url: zod
            .string()
            .url()
            .max(fetchOgpImage200ResponseImageUrlMax)
            .describe("Source og:image URL used for the download"),
        cached: zod.boolean().describe("Whether the OGP metadata came from cache"),
    })
    .strict();

export const FetchOgpImage400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgpImage422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgpImage500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgpImage502Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();

export const FetchOgpImage504Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([
                zod.literal(400),
                zod.literal(404),
                zod.literal(422),
                zod.literal(500),
                zod.literal(502),
                zod.literal(504),
            ])
            .describe("HTTP status code"),
    })
    .strict();
